import { reactive } from 'vue'
import { getPublicClientSysConfig } from '@/api/user'

export const AGENT_CATEGORY_DISPLAY_CONFIG_KEY = 'AGENT_CATEGORY_DISPLAY_CONFIG'

const DEFAULT_PUBLISHED_COUNT = 3
const MAX_PUBLISHED_COUNT = 8
const SUPPORTED_LOCALES = ['zh-cn', 'en-us', 'zh-tw']

export const agentCategoryDisplayConfigState = reactive({
  version: 0,
  enabled: false,
  home: {
    published_count: DEFAULT_PUBLISHED_COUNT
  },
  category_mappings: {},
  loaded: false,
  loading: false
})

let activeRequestId = 0
let activeRequest = null

function parseConfig(rawConfig) {
  if (typeof rawConfig !== 'string') return rawConfig

  try {
    return JSON.parse(rawConfig)
  } catch (error) {
    return null
  }
}

function normalizeHttpsUrl(rawUrl, { requireSvg = false, cssSafe = false } = {}) {
  const value = typeof rawUrl === 'string' ? rawUrl.trim() : ''
  if (!value || value.length > 2048 || /\s/.test(value)) return ''
  if (cssSafe && /["'()\\]/.test(value)) return ''

  try {
    const url = new URL(value)
    if (url.protocol !== 'https:' || !url.hostname || url.username || url.password) return ''
    if (requireSvg && !/\.svg$/i.test(url.pathname)) return ''
    return url.href
  } catch (error) {
    return ''
  }
}

function normalizeColor(rawColor) {
  const color = typeof rawColor === 'string' ? rawColor.trim() : ''
  if (!color) return ''
  if (/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(color)) {
    return color
  }

  const match = color.match(/^rgba?\((.*)\)$/i)
  if (!match) return ''
  const parts = match[1].split(',').map(part => part.trim())
  const expectedParts = /^rgba\(/i.test(color) ? 4 : 3
  if (parts.length !== expectedParts) return ''
  if (!parts.slice(0, 3).every(part => /^\d{1,3}$/.test(part) && Number(part) <= 255)) return ''
  if (expectedParts === 4) {
    const alpha = Number(parts[3])
    if (parts[3] === '' || !Number.isFinite(alpha) || alpha < 0 || alpha > 1) return ''
  }
  return color
}

function normalizeIcon(icon) {
  if (!icon || typeof icon !== 'object' || Array.isArray(icon)) return null

  const type = String(icon.type || '').trim()
  const rawValue = String(icon.value || '').trim()
  const isBuiltin = type === 'builtin' && /^[A-Za-z0-9][A-Za-z0-9_-]*$/.test(rawValue)
  const remoteValue = type === 'remote'
    ? normalizeHttpsUrl(rawValue, { requireSvg: true, cssSafe: true })
    : ''
  const isRemote = Boolean(remoteValue)
  if (!isBuiltin && !isRemote) return null

  const color = normalizeColor(icon.color)
  const size = Number(icon.size)

  return {
    type,
    value: isRemote ? remoteValue : rawValue,
    color,
    size: Number.isInteger(size) && size >= 24 && size <= 48 ? size : null
  }
}

function normalizeHover(hover) {
  if (!hover || typeof hover !== 'object' || Array.isArray(hover) || hover.enabled === false) {
    return null
  }

  const imageUrl = normalizeHttpsUrl(hover.image_url)
  if (!imageUrl) return null

  const rawDescription = hover.description && typeof hover.description === 'object'
    ? hover.description
    : {}
  const description = SUPPORTED_LOCALES.reduce((result, locale) => {
    result[locale] = typeof rawDescription[locale] === 'string'
      ? rawDescription[locale].trim()
      : ''
    return result
  }, {})

  return {
    imageUrl,
    description
  }
}

function normalizeMapping(category, mapping) {
  const normalizedCategory = String(category || mapping?.agent_category || '').trim()
  if (!normalizedCategory || !mapping || typeof mapping !== 'object' || mapping.enabled === false) {
    return null
  }

  return {
    agent_category: normalizedCategory,
    icon: normalizeIcon(mapping.icon),
    hover: normalizeHover(mapping.hover)
  }
}

function normalizeMappings(rawMappings) {
  if (!rawMappings || typeof rawMappings !== 'object') return {}

  if (Array.isArray(rawMappings)) {
    return rawMappings.reduce((result, mapping) => {
      const normalized = normalizeMapping(mapping?.agent_category, mapping)
      if (normalized) result[normalized.agent_category] = normalized
      return result
    }, {})
  }

  return Object.entries(rawMappings).reduce((result, [category, mapping]) => {
    const normalized = normalizeMapping(category, mapping)
    if (normalized) result[normalized.agent_category] = normalized
    return result
  }, {})
}

function normalizeConfig(rawConfig) {
  const parsedConfig = parseConfig(rawConfig)
  const source = parsedConfig?.value && typeof parsedConfig.value === 'object'
    ? parsedConfig.value
    : parsedConfig

  if (!source || typeof source !== 'object' || Array.isArray(source)) {
    return {
      version: 0,
      enabled: false,
      publishedCount: DEFAULT_PUBLISHED_COUNT,
      categoryMappings: {}
    }
  }

  const configuredPublishedCount = Number(source.home?.published_count)

  return {
    version: Number(source.version) || 1,
    enabled: source.enabled !== false,
    publishedCount: Number.isInteger(configuredPublishedCount) && configuredPublishedCount >= 0 && configuredPublishedCount <= MAX_PUBLISHED_COUNT
      ? configuredPublishedCount
      : DEFAULT_PUBLISHED_COUNT,
    categoryMappings: normalizeMappings(source.category_mappings)
  }
}

function applyConfig(rawConfig) {
  const normalized = normalizeConfig(rawConfig)
  agentCategoryDisplayConfigState.version = normalized.version
  agentCategoryDisplayConfigState.enabled = normalized.enabled
  agentCategoryDisplayConfigState.home = {
    published_count: normalized.publishedCount
  }
  agentCategoryDisplayConfigState.category_mappings = normalized.categoryMappings
  agentCategoryDisplayConfigState.loaded = true
}

export function loadAgentCategoryDisplayConfig({ force = false } = {}) {
  if (activeRequest && !force) return activeRequest

  const requestId = ++activeRequestId
  agentCategoryDisplayConfigState.loading = true

  const request = getPublicClientSysConfig({ key: AGENT_CATEGORY_DISPLAY_CONFIG_KEY })
    .then(response => {
      if (requestId === activeRequestId) {
        const responseData = response?.code === 0 ? response.data : null
        // Public config endpoints may return the config directly or wrap it
        // in a data field. Keep the client tolerant of either contract.
        const config = responseData?.data && typeof responseData.data === 'object'
          ? responseData.data
          : responseData
        applyConfig(config)
      }
      return agentCategoryDisplayConfigState
    })
    .catch(error => {
      if (requestId === activeRequestId && !agentCategoryDisplayConfigState.loaded) {
        applyConfig(null)
      }
      throw error
    })
    .finally(() => {
      if (requestId === activeRequestId) {
        agentCategoryDisplayConfigState.loading = false
        activeRequest = null
      }
    })

  activeRequest = request
  return request
}

export function resetAgentCategoryDisplayConfig() {
  activeRequestId += 1
  activeRequest = null
  agentCategoryDisplayConfigState.loading = false
  applyConfig(null)
}

export function getAgentCategoryMapping(category) {
  if (!agentCategoryDisplayConfigState.enabled) return null

  const normalizedCategory = String(category || '').trim()
  return normalizedCategory
    ? agentCategoryDisplayConfigState.category_mappings[normalizedCategory] || null
    : null
}

export function getAgentCategoryHoverMeta(category) {
  return getAgentCategoryMapping(category)?.hover || null
}

export function getAgentCategoryHoverDescription(category, locale) {
  const description = getAgentCategoryHoverMeta(category)?.description
  if (!description) return ''

  const normalizedLocale = String(locale || '').toLowerCase().replace('_', '-')
  return description[normalizedLocale] || description['zh-cn'] || description['en-us'] || description['zh-tw'] || ''
}

export function getPublishedAgentCount() {
  return agentCategoryDisplayConfigState.enabled
    ? agentCategoryDisplayConfigState.home.published_count
    : DEFAULT_PUBLISHED_COUNT
}
